import React from 'react';
import { Form } from 'react-bootstrap';
import {
  ApproverQuestion,
  ApproverQuestionAnswerInput,
  ApproverQuestionAnswerRecord,
  formatApproverAnswer,
} from '../utils/approverQuestions';

export type ApproverAnswersState = Record<string, ApproverQuestionAnswerInput>;

interface ApproverQuestionsFormProps {
  questions: ApproverQuestion[];
  answers: ApproverAnswersState;
  onChange: (answers: ApproverAnswersState) => void;
  disabled?: boolean;
}

/** Perguntas adicionais respondidas pelo aprovador ao aprovar ou reprovar. */
const ApproverQuestionsForm: React.FC<ApproverQuestionsFormProps> = ({
  questions,
  answers,
  onChange,
  disabled = false,
}) => {
  if (questions.length === 0) return null;

  const setTextAnswer = (question: ApproverQuestion, value: string) => {
    onChange({ ...answers, [question.id]: { question_id: question.id, answer: value } });
  };

  const toggleOption = (question: ApproverQuestion, option: string, checked: boolean) => {
    const current = answers[question.id]?.selected_options || [];
    let selected: string[];
    if (question.allow_multiple) {
      selected = checked ? [...current, option] : current.filter((item) => item !== option);
    } else {
      selected = checked ? [option] : [];
    }
    onChange({
      ...answers,
      [question.id]: { question_id: question.id, selected_options: selected },
    });
  };

  return (
    <div className="mb-3">
      {questions.map((question) => (
        <Form.Group className="mb-3" key={question.id}>
          <Form.Label>
            {question.prompt} <span className="text-danger">*</span>
          </Form.Label>

          {question.answer_type === 'options' ? (
            <div>
              {question.choices.map((choice) => (
                <Form.Check
                  key={`${question.id}-${choice}`}
                  type={question.allow_multiple ? 'checkbox' : 'radio'}
                  id={`${question.id}-${choice}`}
                  name={question.id}
                  label={choice}
                  disabled={disabled}
                  checked={(answers[question.id]?.selected_options || []).includes(choice)}
                  onChange={(e) => toggleOption(question, choice, e.target.checked)}
                />
              ))}
              {question.allow_multiple && (
                <Form.Text className="text-muted">Você pode marcar mais de uma opção.</Form.Text>
              )}
            </div>
          ) : (
            <Form.Control
              as="textarea"
              rows={3}
              disabled={disabled}
              value={answers[question.id]?.answer || ''}
              onChange={(e) => setTextAnswer(question, e.target.value)}
              placeholder="Digite sua resposta"
            />
          )}
        </Form.Group>
      ))}
    </div>
  );
};

interface ApproverQuestionAnswersProps {
  records: ApproverQuestionAnswerRecord[];
}

/** Leitura das perguntas e respostas registradas na decisão. */
export const ApproverQuestionAnswers: React.FC<ApproverQuestionAnswersProps> = ({ records }) => {
  if (records.length === 0) return null;

  return (
    <div className="d-flex flex-column gap-2">
      {records.map((record) => (
        <div key={record.id}>
          <div className="small text-muted">{record.prompt}</div>
          <div className="small">{formatApproverAnswer(record) || '-'}</div>
        </div>
      ))}
    </div>
  );
};

export default ApproverQuestionsForm;
